import {
  buildAssetFilterRules,
  type AssetFilterRules,
  type BuildAssetFilterRulesInput,
} from './asset-filter-rules';
import {
  type ScopeDimension,
  domainEntryMatchesUrl,
  findMatchingDomainEntry,
  isHostCoveredByAnyApplicationDomain,
  isHostCoveredByScopedDomains,
  splitDomainHostAndPath,
} from './domain-scope';
import { isKnownThirdPartyHost } from './known-third-party-domains';
import {
  extractHostname,
  extractRequestScopePath,
  isPathOnlyUrl,
  isSubdomain,
  isUnsupportedScheme,
} from './url-pattern-matching';

export enum ApplicationUrlScopeAllowVerdict {
  InScope = 'in_scope',
  UnknownHost = 'unknown_host',
}

export enum ApplicationUrlScopeSkipReason {
  KnownHostOutOfScope = 'known_host_out_of_scope',
  Unsupported = 'unsupported',
  Invalid = 'invalid',
  NoiseDomain = 'noise_domain',
  NoiseUrlPath = 'noise_url_path',
  NonCapturableContentType = 'non_capturable_content_type',
}

export type ApplicationUrlScopeVerdict =
  | ApplicationUrlScopeAllowVerdict
  | ApplicationUrlScopeSkipReason;

const APPLICATION_URL_SCOPE_SKIP_REASONS = new Set<string>(
  Object.values(ApplicationUrlScopeSkipReason),
);

export function isApplicationUrlScopeSkipReason(
  verdict: ApplicationUrlScopeVerdict,
): verdict is ApplicationUrlScopeSkipReason {
  return APPLICATION_URL_SCOPE_SKIP_REASONS.has(verdict);
}

export type ApplicationUrlScopeLogVerdict = (
  message: string,
  data?: { props?: Record<string, unknown> },
) => void;

export const noopApplicationUrlScopeLogVerdict: ApplicationUrlScopeLogVerdict =
  () => undefined;

export type ShouldCaptureUrlParams = BuildAssetFilterRulesInput & {
  url: string;
  contentType?: string;
  responseStatus?: number;
  allowNotFoundResponse?: boolean;
  skipNoiseUrlExtensions?: boolean;
  logVerdict: ApplicationUrlScopeLogVerdict;
};

function resolvedAssetRules(
  source: AssetFilterRules | BuildAssetFilterRulesInput,
): AssetFilterRules {
  const noiseDomains = (source as Partial<AssetFilterRules>).noiseDomains;

  if (Array.isArray(noiseDomains)) {
    return source as AssetFilterRules;
  }

  return buildAssetFilterRules(source as BuildAssetFilterRulesInput);
}

type ClassifyDomainScopeParams = {
  rules: AssetFilterRules;
  url: string;
  dimension: ScopeDimension;
};

function classifyDomainScope({
  rules,
  url,
  dimension,
}: ClassifyDomainScopeParams): ApplicationUrlScopeVerdict {
  if (isUnsupportedScheme(url)) {
    return ApplicationUrlScopeSkipReason.Unsupported;
  }

  const host = extractHostname(url);

  if (!host) {
    return url.trim().startsWith('/')
      ? ApplicationUrlScopeAllowVerdict.InScope
      : ApplicationUrlScopeSkipReason.Invalid;
  }

  const requestPath = extractRequestScopePath(url);

  if (host === 'localhost') {
    return isLocalhostInScope(rules, dimension)
      ? ApplicationUrlScopeAllowVerdict.InScope
      : ApplicationUrlScopeSkipReason.KnownHostOutOfScope;
  }

  if (rules.applicationDomains.length > 0) {
    const matched = findMatchingDomainEntry({
      applicationDomains: rules.applicationDomains,
      requestHost: host,
      requestPath,
      dimension,
    });

    if (matched) {
      return ApplicationUrlScopeAllowVerdict.InScope;
    }

    if (
      isHostCoveredByScopedDomains({
        applicationDomains: rules.applicationDomains,
        requestHost: host,
        dimension,
      })
    ) {
      return ApplicationUrlScopeSkipReason.KnownHostOutOfScope;
    }

    if (
      isHostCoveredByAnyApplicationDomain({
        applicationDomains: rules.applicationDomains,
        requestHost: host,
      })
    ) {
      return ApplicationUrlScopeSkipReason.KnownHostOutOfScope;
    }

    return ApplicationUrlScopeAllowVerdict.UnknownHost;
  }

  return ApplicationUrlScopeAllowVerdict.UnknownHost;
}

type ClassifyUrlScopeParams = {
  rules: AssetFilterRules;
  url: string;
  contentType?: string;
  dimension: ScopeDimension;
  skipNoiseUrlExtensions?: boolean;
};

function classifyUrlScope({
  rules,
  url,
  contentType,
  dimension,
  skipNoiseUrlExtensions,
}: ClassifyUrlScopeParams): ApplicationUrlScopeVerdict {
  const domainScope = classifyDomainScope({ rules, url, dimension });

  if (
    domainScope === ApplicationUrlScopeSkipReason.Unsupported ||
    domainScope === ApplicationUrlScopeSkipReason.Invalid
  ) {
    return domainScope;
  }

  const host = extractHostname(url);

  if (!host) {
    if (!url.trim().startsWith('/')) {
      return ApplicationUrlScopeSkipReason.Invalid;
    }

    if (matchesNoiseUrlPath(url, rules, skipNoiseUrlExtensions)) {
      return ApplicationUrlScopeSkipReason.NoiseUrlPath;
    }

    if (contentType && !isCapturableContentType(contentType)) {
      return ApplicationUrlScopeSkipReason.NonCapturableContentType;
    }

    return domainScope;
  }

  if (
    domainScope !== ApplicationUrlScopeAllowVerdict.InScope &&
    matchesNoiseDomain(host, rules.noiseDomains) &&
    !isKnownThirdPartyHost(host) &&
    !isInApplicationScope({ source: rules, url, dimension: 'thirdParty' })
  ) {
    return ApplicationUrlScopeSkipReason.NoiseDomain;
  }

  if (matchesNoiseUrlPath(url, rules, skipNoiseUrlExtensions)) {
    return ApplicationUrlScopeSkipReason.NoiseUrlPath;
  }

  if (contentType && !isCapturableContentType(contentType)) {
    return ApplicationUrlScopeSkipReason.NonCapturableContentType;
  }

  return domainScope;
}

export function getApplicationUrlScopeVerdict({
  applicationUrl,
  domains,
  url,
  contentType,
  skipNoiseUrlExtensions,
}: Omit<
  ShouldCaptureUrlParams,
  'logVerdict' | 'responseStatus'
>): ApplicationUrlScopeVerdict {
  const rules = resolvedAssetRules({
    applicationUrl,
    domains,
  });

  return classifyUrlScope({
    rules,
    url,
    contentType,
    dimension: 'functionality',
    skipNoiseUrlExtensions,
  });
}

export function shouldCaptureUrl({
  applicationUrl,
  domains,
  url,
  contentType,
  responseStatus,
  allowNotFoundResponse,
  skipNoiseUrlExtensions,
  logVerdict,
}: ShouldCaptureUrlParams): boolean {
  if (responseStatus === 404 && allowNotFoundResponse !== true) return false;

  const scope = getApplicationUrlScopeVerdict({
    applicationUrl,
    domains,
    url,
    contentType,
    skipNoiseUrlExtensions,
  });

  if (
    isApplicationUrlScopeSkipReason(scope) &&
    !(
      scope === ApplicationUrlScopeSkipReason.KnownHostOutOfScope &&
      !applicationUrl
    )
  ) {
    if (
      scope === ApplicationUrlScopeSkipReason.KnownHostOutOfScope &&
      applicationUrl &&
      isInApplicationScope({
        source: { applicationUrl, domains },
        url,
        dimension: 'thirdParty',
      })
    ) {
      return true;
    }

    logVerdict('Application URL scope verdict', {
      props: { scopeVerdict: scope, url },
    });

    return false;
  }

  if (!applicationUrl) {
    return true;
  }

  return (
    scope === ApplicationUrlScopeAllowVerdict.InScope ||
    scope === ApplicationUrlScopeAllowVerdict.UnknownHost
  );
}

export function isNonNavigableAssetUrl(
  source: AssetFilterRules | BuildAssetFilterRulesInput,
  url: string,
): boolean {
  if (isUnsupportedScheme(url)) return true;

  const rules = resolvedAssetRules(source);

  return matchesNoiseUrlPath(url, rules);
}

export type IsInApplicationScopeParams = {
  source: AssetFilterRules | BuildAssetFilterRulesInput;
  url: string;
  dimension: ScopeDimension;
};

export function isInApplicationScope({
  source,
  url,
  dimension,
}: IsInApplicationScopeParams): boolean {
  const rules = resolvedAssetRules(source);

  return (
    classifyDomainScope({ rules, url, dimension }) ===
    ApplicationUrlScopeAllowVerdict.InScope
  );
}

export type IsInAnyApplicationScopeParams = {
  source: AssetFilterRules | BuildAssetFilterRulesInput;
  url: string;
};

export function isInAnyApplicationScope({
  source,
  url,
}: IsInAnyApplicationScopeParams): boolean {
  return (
    isInApplicationScope({ source, url, dimension: 'testing' }) ||
    isInApplicationScope({ source, url, dimension: 'functionality' })
  );
}

export type ShouldAttachApplicationGlobalHeadersParams = {
  url: string;
  applicationUrl: string;
  domains: BuildAssetFilterRulesInput['domains'];
};

export function shouldAttachApplicationGlobalHeaders({
  url,
  applicationUrl,
  domains,
}: ShouldAttachApplicationGlobalHeadersParams): boolean {
  if (!applicationUrl.trim() || !url.trim()) {
    return false;
  }

  return isInApplicationScope({
    source: { applicationUrl, domains },
    url,
    dimension: 'testing',
  });
}

export const SIGNAL_OUT_OF_TESTING_SCOPE_ERROR = 'SIGNAL_OUT_OF_TESTING_SCOPE';

export function proposeDomainIfNew(
  source: AssetFilterRules | BuildAssetFilterRulesInput,
  url: string,
): string | null {
  const rules = resolvedAssetRules(source);
  const scope = classifyDomainScope({ rules, url, dimension: 'functionality' });

  if (scope !== ApplicationUrlScopeAllowVerdict.UnknownHost) {
    return null;
  }

  const host = extractHostname(url);

  if (!host || host === 'localhost') {
    return null;
  }

  if (
    matchesNoiseDomain(host, rules.noiseDomains) &&
    !isKnownThirdPartyHost(host)
  ) {
    return null;
  }

  return host;
}

function isLocalhostInScope(
  rules: AssetFilterRules,
  dimension: ScopeDimension,
): boolean {
  if (
    dimension !== 'thirdParty' &&
    rules.applicationUrl?.startsWith('http://localhost')
  ) {
    return true;
  }

  return rules.applicationDomains.some((entry) => {
    const { host } = splitDomainHostAndPath(entry.domain);

    if (host !== 'localhost') {
      return false;
    }

    return domainEntryMatchesUrl({
      entry,
      requestHost: 'localhost',
      requestPath: '/',
      dimension,
    });
  });
}

function matchesNoiseDomain(host: string, noiseDomains: string[]): boolean {
  const normalized = host.toLowerCase();

  for (const noise of noiseDomains) {
    if (normalized === noise || isSubdomain(normalized, noise)) return true;
  }

  return false;
}

function matchesNoiseUrlPath(
  url: string,
  rules: AssetFilterRules,
  skipNoiseUrlExtensions?: boolean,
): boolean {
  let pathname: string;
  const trimmed = url.trim();

  try {
    const parseable = trimmed.startsWith('//') ? `https:${trimmed}` : trimmed;

    pathname = new URL(parseable).pathname.toLowerCase();
  } catch {
    if (!isPathOnlyUrl(trimmed)) {
      return false;
    }

    pathname = (trimmed.split(/[?#]/)[0] || '/').toLowerCase();
  }

  for (const prefix of rules.noiseUrlPathPrefixes) {
    if (pathname === prefix || pathname.startsWith(prefix)) return true;
  }

  if (!skipNoiseUrlExtensions) {
    const lastDot = pathname.lastIndexOf('.');

    if (lastDot !== -1) {
      const ext = pathname.slice(lastDot);

      if (rules.noiseUrlExtensions.includes(ext)) return true;
    }
  }

  for (const segment of rules.noiseUrlPathSegments) {
    if (pathname.includes(segment)) return true;
  }

  if (rules.noiseUrlSubstrings.length > 0) {
    const lowercased = url.toLowerCase();

    for (const substring of rules.noiseUrlSubstrings) {
      if (lowercased.includes(substring)) return true;
    }
  }

  return false;
}

const NON_TRACKABLE_CONTENT_TYPE_PREFIXES = [
  'image/',
  'font/',
  'audio/',
  'video/',
];

function isCapturableContentType(contentType: string): boolean {
  if (!contentType) return true;
  const normalized = contentType.toLowerCase().split(';')[0].trim();

  for (const prefix of NON_TRACKABLE_CONTENT_TYPE_PREFIXES) {
    if (normalized.startsWith(prefix)) return false;
  }

  return true;
}
